/* eslint-disable max-lines, complexity, max-lines-per-function */ // NOSONAR
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { customDropdownStyles } from '../../data/data';
import Select from 'react-select';
import { useApiServiceForecast } from '../../services/apiServiceForecast';
import toast from 'react-hot-toast';
import { formValidationSchemaModels } from '../../schema/formValidationSchema';
import { formatDate, isTrainingInProgressStatus } from '../../utils/utils';
import TimeSeriesChart from '../../charts/timeseriesChart';
import ForecastFormView from './ForecastFormView';
const ForecastForm = () => { // NOSONAR
    const [isForecastSelection, setIsForecastSelection] = useState(false);
    const [isBusinessRules, setIsBusinessRules] = useState(false);
    const [frequency, setFrequency] = useState(30);
    const [loading, setLoading] = useState(true);
    const [selectedAlias, setSelectedAlias] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [businessRuleValue, setBusinessRuleValue] = useState(0);
    const [businessRuleName, setBusinessRuleName] = useState('');
    const [businessRules, setBusinessRules] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [editAlias, setEditAlias] = useState(null);
    const [editOperator, setEditOperator] = useState(null);
    const [editValue, setEditValue] = useState(0);
    const [editName, setEditName] = useState('');
    const [chartLoading, setChartLoading] = useState(false);
    const [forecastChartLoading, setForecastChartLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [forecastTimeseriesData, setForecastTimeseriesData] = useState(null);
    const [forecastTagAlias, setForecastTagAlias] = useState([]);
    const [forecastChartData, setForecastChartData] = useState(null);
    const [liveForecastsData, setLiveForecastsData] = useState([]);
    const [selectedModelName, setSelectedModelName] = useState(null);
    const [showForecastChart, setShowForecastChart] = useState(false);
    const [isNoForecastDataPopup, setIsNoForecastDataPopup] = useState(false);
    const [isNoExistingModelsPopup, setIsNoExistingModelsPopup] = useState(false);
    const [isNoModelsFoundPopup, setIsNoModelsFoundPopup] = useState(false);
    const [selectedAnomaly, setSelectedAnomaly] = useState(null);
    const skipFetchRef = useRef(false);
    const [dropdownOptions, setDropdownOptions] = useState({
        assemblyOptions: [],
        dryGasSealTagsOptions: [],
        independentTagsOptions: []
    });
    const [selectedValues, setSelectedValues] = useState({
        selectedAssembly: null,
        selectedDryGasSealTags: null,
        selectedIndependentTags: null
    });
    const [nameDesc, setNameDesc] = useState({
        name: '',
        desc: ''
    });
    const selectedTagOrder = useMemo(() => {
        let dgsAliases = [];
        if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
            dgsAliases = JSON.parse(selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"'));
        }
        let independentAliases = [];
        if (Array.isArray(selectedValues.selectedIndependentTags)) {
            independentAliases = selectedValues.selectedIndependentTags.map(option => option.tagAlias).filter(Boolean);
        }
        return [...dgsAliases, ...independentAliases];
    }, [selectedValues.selectedDryGasSealTags, selectedValues.selectedIndependentTags]);
    const forecastTagOrder = useMemo(
        () => forecastChartData?.tag_aliases ?? selectedTagOrder,
        [forecastChartData, selectedTagOrder]
    );
    // Calculate default dates
    const today = new Date();
    const trainingEndDate = new Date(today);
    // 1 day before today.
    trainingEndDate.setDate(trainingEndDate.getDate() - 1);
    const trainingStartDate = new Date(today);
    // 6 months before today.
    trainingStartDate.setMonth(trainingStartDate.getMonth() - 6);
    const futureStartDate = new Date(today);
    // 1 day before today (yesterday).
    futureStartDate.setDate(futureStartDate.getDate() - 1);
    const futureEndDate = new Date(futureStartDate);
    // 3 months from start date.
    futureEndDate.setMonth(futureEndDate.getMonth() + 3);
    const [trainingStartDateState, setTrainingStartDate] = useState(formatDate(trainingStartDate));
    const [trainingEndDateState, setTrainingEndDate] = useState(formatDate(trainingEndDate));
    const [futureStartDateState, setFutureStartDate] = useState(formatDate(futureStartDate));
    const [futureEndDateState, setFutureEndDate] = useState(formatDate(futureEndDate));
    const toggleForecastSelection = () => {
        setIsForecastSelection(prev => !prev);
    };
    const toggleBusinessRule = () => {
        setIsBusinessRules(prev => !prev);
    };
    const {
        getAssemblies,
        getDryGasSealTags,
        getIndependentTags,
        getTimeSeriesChartData,
        startForecast,
        getForecastData,
        getForecastModelList,
        deleteForecastModel,
        updateForecastModelStatus
    } = useApiServiceForecast();
    const fetchDropdownData = async (
        dropdownName,
        apiCall,
        mapFunction,
        datasource = null
    ) => {
        try {
            const data = await apiCall(datasource);
            if (Array.isArray(data)) {
                const options = data.map(mapFunction);
                const uniqueOptions = Array.from(
                    new Set(options.map(option => option.value))
                ).map(value => options.find(option => option.value === value));
                setDropdownOptions(prev => ({
                    ...prev,
                    [`${dropdownName}Options`]: uniqueOptions
                }));
            }
        } catch (error) {
            toast.error(
                `Error fetching ${dropdownName} data. Please try again later.`
            );
            console.error(
                `Error fetching ${dropdownName} data in Models Screen:`,
                error
            );
            return null;
        }
    };
    const mapAssembly = item => ({
        label: `${item.DRIVER} ${item.PROJECTNAME}`,
        value: `${item.DRIVER} ${item.PROJECTNAME}`,
        datasource: item.DATASOURCE
    });
    const mapDryGasSealTags = item => {
        const depTagAliasArray = item.TAG_ALIAS_LIST.replace(/[[\]']/g, '').split(
            ', '
        );
        const formattedString = depTagAliasArray.map(tag => `${tag}`).join(',');
        return {
            label: formattedString,
            value: formattedString,
            tagName: item.TAG_NAME,
            modelId: item.MODEL_ID,
            datasource: item.DATASOURCE_ID,
            tagAliasList: item.TAG_ALIAS_LIST
        };
    };
    const mapIndependentTags = item => ({
        label: item.TAG_NAME,
        value: item.TAG_NAME,
        datasource: item.DATASOURCE,
        tagAlias: item.INDEP_TAG_ALIAS
    });
    const getDgsTagAliases = dryGasTag => {
        if (!dryGasTag?.tagAliasList) {
            return [];
        }
        try {
            return JSON.parse(dryGasTag.tagAliasList.replace(/'/g, '"'));
        } catch (error) {
            console.error('Error parsing DGS tag aliases:', error);
            return [];
        }
    };
    const getIndependentTagAliases = independentTags => {
        if (!Array.isArray(independentTags)) {
            return [];
        }
        return independentTags.map(option => option.tagAlias).filter(Boolean);
    };
    const getCombinedTagAliases = (dryGasTag, independentTags) => [
        ...getDgsTagAliases(dryGasTag),
        ...getIndependentTagAliases(independentTags)
    ];
    const getDatasource = (assembly, dryGasTag, independentTags) => {
        if (dryGasTag?.datasource) {
            return dryGasTag.datasource;
        }
        if (assembly?.datasource) {
            return assembly.datasource;
        }
        if (Array.isArray(independentTags) && independentTags.length > 0) {
            return independentTags[0]?.datasource;
        }
        return null;
    };
    useEffect(() => {
        const fetchData = async () => {
            await fetchDropdownData('assembly', getAssemblies, mapAssembly);
            setLoading(false);
        };
        fetchData();
    }, []);
    // Fetch chart data when frequency, startDateState, endDateState changes
    useEffect(() => {
        const fetchChartData = async () => {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            if (
                selectedValues.selectedAssembly &&
                datasource &&
                combinedTagAliases.length > 0
            ) {
                // Prepare business_rules array for API
                const business_rules = businessRules.map(rule => ({
                    ALIAS: rule.alias?.value || rule.alias,
                    OPERATOR: rule.operator?.value || rule.operator,
                    VALUE: rule.value
                }));
                setChartLoading(true);
                try {
                    const data = await getTimeSeriesChartData(
                        trainingStartDateState,
                        trainingEndDateState,
                        datasource,
                        combinedTagAliases,
                        frequency,
                        business_rules
                    );
                    setForecastTimeseriesData(data);
                } catch (error) {
                    // Clear data on error to prevent rendering with invalid data.
                    setForecastTimeseriesData(null);
                    setChartLoading(false);
                    toast.error(
                        'Error fetching time series chart data. Please try again.'
                    );
                    console.error('Error fetching time series chart data:', error);
                } finally {
                    setChartLoading(false);
                }
            }
        };
        fetchChartData();
    }, [
        frequency,
        trainingStartDateState,
        trainingEndDateState,
        selectedValues.selectedDryGasSealTags,
        selectedValues.selectedIndependentTags
    ]);
    // Auto-select all tag aliases by default when Dry Gas Seal Tags or Independent Tags are selected
    useEffect(() => {
        const allTagOptions = [];
        // Add DGS tag aliases
        if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
            const tagAliases = JSON.parse(
                selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
            );
            const dgsTagOptions = tagAliases.map(tag => ({
                label: tag,
                value: tag
            }));
            allTagOptions.push(...dgsTagOptions);
        }
        // Add independent tag aliases
        if (selectedValues.selectedIndependentTags && Array.isArray(selectedValues.selectedIndependentTags)) {
            const independentTagOptions = selectedValues.selectedIndependentTags.map(option => ({
                label: option.tagAlias,
                value: option.tagAlias
            }));
            allTagOptions.push(...independentTagOptions);
        }
        setForecastTagAlias(allTagOptions);
    }, [selectedValues.selectedDryGasSealTags, selectedValues.selectedIndependentTags]);
    // Reload forecast chart data when future dates change
    useEffect(() => {
        // Skip fetch if it was just called from handleViewForecastFromTable
        if (skipFetchRef.current) {
            skipFetchRef.current = false;
            return;
        }
        const fetchForecastChartData = async () => {
            if (selectedModelName) {
                setForecastChartLoading(true);
                try {
                    const data = await getForecastData(
                        futureStartDateState,
                        futureEndDateState,
                        selectedModelName
                    );
                    setForecastChartData(data);
                } catch (error) {
                    console.error('Error fetching forecast data:', error);
                    toast.error('Failed to fetch forecast data');
                } finally {
                    setForecastChartLoading(false);
                }
            }
        };
        fetchForecastChartData();
    }, [futureStartDateState, futureEndDateState]);
    // Auto-reload forecast models while any model is in progress
    useEffect(() => {
        // Check every 15 seconds.
        const interval = setInterval(() => {
            if (liveForecastsData.some(model => isTrainingInProgressStatus(model.TRAINING_STATUS))) {
                reloadForecastModels();
            }
        }, 15000);
        // Clear the interval on component unmount
        return () => clearInterval(interval);
    }, [liveForecastsData]);
    const validateField = (fieldName, value) => {
        formValidationSchemaModels
            .validateAt(fieldName, { [fieldName]: value })
            .then(() => {
                setErrors(prevErrors => ({ ...prevErrors, [fieldName]: null }));
            })
            .catch(validationError => {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [fieldName]: validationError.message
                }));
            });
    };
    const handleSelectChange = async (name, selectedOption) => { // NOSONAR
        if (name === 'selectedAssembly') {
            setSelectedValues(prev => ({
                ...prev,
                selectedAssembly: selectedOption,
                selectedDryGasSealTags: null,
                selectedIndependentTags: null
            }));
            // Hide the forecast chart when assembly changes
            setShowForecastChart(false);
            setForecastChartData(null);
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);
            setLiveForecastsData([]);
        } else if (name === 'selectedDryGasSealTags') {
            setSelectedValues(prev => ({
                ...prev,
                selectedDryGasSealTags: selectedOption
            }));
            // Hide the forecast chart when DGS tags change
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);
        } else if (name === 'selectedIndependentTags') {
            setSelectedValues(prev => ({
                ...prev,
                selectedIndependentTags: selectedOption
            }));
            // Hide the forecast chart when Independent tags change
            setShowForecastChart(false);
            setForecastChartData(null);
            // Clear the timeseries chart data to prevent rendering with stale data
            setForecastTimeseriesData(null);
        } else {
            setSelectedValues(prev => ({ ...prev, [name]: selectedOption }));
        }
        const fieldsToValidate = ['selectedAssembly', 'selectedDryGasSealTags'];
        if (fieldsToValidate.includes(name)) {
            validateField(name, selectedOption);
        }
        if (name === 'selectedAssembly' && selectedOption) {
            const datasource = selectedOption.datasource;
            setLoading(true);
            try {
                await Promise.all([
                    fetchDropdownData(
                        'dryGasSealTags',
                        getDryGasSealTags,
                        mapDryGasSealTags,
                        datasource
                    ),
                    fetchDropdownData(
                        'independentTags',
                        getIndependentTags,
                        mapIndependentTags,
                        datasource
                    )
                ]);
            } catch (e) {
                toast.error(
                    'Error fetching tags. Please try again later.',
                    e
                );
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }
        if (name === 'selectedDryGasSealTags' || name === 'selectedIndependentTags') {
            let nextDryGasTag = selectedValues.selectedDryGasSealTags;
            if (name === 'selectedDryGasSealTags') {
                nextDryGasTag = selectedOption;
            }
            let nextIndependentTags = selectedValues.selectedIndependentTags;
            if (name === 'selectedIndependentTags') {
                nextIndependentTags = selectedOption;
            }
            const combinedTagAliases = getCombinedTagAliases(nextDryGasTag, nextIndependentTags);
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                nextDryGasTag,
                nextIndependentTags
            );
            if (!selectedValues.selectedAssembly || !datasource || combinedTagAliases.length === 0) {
                setLiveForecastsData([]);
                return;
            }
            setLoading(true);
            try {
                // Fetch live forecasts data
                const forecastsData = await getForecastModelList(
                    datasource,
                    combinedTagAliases
                );
                // Check if forecast list data is empty
                if (!forecastsData || !Array.isArray(forecastsData) || forecastsData.length === 0) {
                    setIsNoModelsFoundPopup(true);
                }
                setLiveForecastsData(forecastsData);
            } catch (error) {
                setLoading(false);
                toast.error(
                    'Error fetching forecast models. Please try again.'
                );
                console.error('Error fetching forecast models:', error);
            } finally {
                setLoading(false);
            }
        }
    };
    const isBusinessRuleAddEnabled =
        selectedAlias &&
        selectedOperator &&
        String(businessRuleValue).trim() !== '' &&
        businessRuleName.trim() !== '';
    const handleAddBusinessRule = () => {
        const newRule = {
            alias: selectedAlias,
            operator: selectedOperator,
            value: Number(businessRuleValue),
            name: businessRuleName
        };
        if (editIndex !== null) {
            // Edit mode
            setBusinessRules(rules => rules.map((rule, index) => {
                if (index === editIndex) {
                    return newRule;
                }
                return rule;
            }));
            setEditIndex(null);
        } else {
            // Add mode
            setBusinessRules(rules => [...rules, newRule]);
        }
        // Clear the input fields after add/update
        setSelectedAlias(null);
        setSelectedOperator(null);
        setBusinessRuleValue(0);
        setBusinessRuleName('');
    };
    const handleDeleteBusinessRule = idx => {
        setBusinessRules(rules => rules.filter((_, i) => i !== idx));
    };
    const handleEditBusinessRule = idx => {
        const rule = businessRules[idx];
        setEditAlias(rule.alias);
        setEditOperator(rule.operator);
        setEditValue(rule.value);
        setEditName(rule.name);
        setEditIndex(idx);
    };
    // Add this function inside TrainingForm
    const handleClearAllBusinessRules = async () => {
        setBusinessRules([]);
        setSelectedAlias(null);
        setSelectedOperator(null);
        setBusinessRuleValue(0);
        setBusinessRuleName('');
        setEditIndex(null);
        // Call chart API again with no business rules
        if (
            selectedValues.selectedAssembly
        ) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            if (!datasource || combinedTagAliases.length === 0) {
                return;
            }
            setChartLoading(true);
            setLoading(true);
            try {
                const data = await getTimeSeriesChartData(
                    trainingStartDateState,
                    trainingEndDateState,
                    datasource,
                    combinedTagAliases,
                    frequency,
                    []
                );
                setForecastTimeseriesData(data);
            } catch (error) {
                // Clear data on error.
                setForecastTimeseriesData(null);
                setLoading(false);
                setChartLoading(false);
                console.error('Error while clearing business rules:', error);
            } finally {
                setLoading(false);
                setChartLoading(false);
            }
        }
    };
    // Add this function inside TrainingForm
    const handleSaveEditBusinessRule = () => {
        const updatedRule = {
            alias: editAlias,
            operator: editOperator,
            value: Number(editValue),
            name: editName
        };
        setBusinessRules(rules => rules.map((rule, index) => {
            if (index === editIndex) {
                return updatedRule;
            }
            return rule;
        }));
        setEditIndex(null);
        setEditAlias(null);
        setEditOperator(null);
        setEditValue('');
        setEditName('');
    };
    const handleApplyBusinessRules = async () => {
        if (
            selectedValues.selectedAssembly
        ) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            if (!datasource || combinedTagAliases.length === 0) {
                return;
            }
            const business_rules = businessRules.map(rule => ({
                ALIAS: rule.alias?.value || rule.alias,
                OPERATOR: rule.operator?.value || rule.operator,
                VALUE: rule.value
            }));
            setChartLoading(true);
            setLoading(true);
            try {
                const data = await getTimeSeriesChartData(
                    trainingStartDateState,
                    trainingEndDateState,
                    datasource,
                    combinedTagAliases,
                    frequency,
                    business_rules
                );
                setForecastTimeseriesData(data);
            } catch (error) {
                // Clear data on error.
                setForecastTimeseriesData(null);
                toast.error('Error applying business rules. Please try again.');
                setLoading(false);
                setChartLoading(false);
                console.error('Error applying business rules:', error);
            } finally {
                setLoading(false);
                setChartLoading(false);
            }
        }
    };
    const clearFormData = () => {
        // Clear form data after forecast starts (keep tags selected)
        setBusinessRules([]);
        setErrors({});
        setNameDesc({ name: '', desc: '' });
        setForecastChartData(null);
        setShowForecastChart(false);
    };
    const handleForecast = async () => {
        const combinedTagAliases = getCombinedTagAliases(
            selectedValues.selectedDryGasSealTags,
            selectedValues.selectedIndependentTags
        );
        const datasource = getDatasource(
            selectedValues.selectedAssembly,
            selectedValues.selectedDryGasSealTags,
            selectedValues.selectedIndependentTags
        );
        if (!datasource || combinedTagAliases.length === 0) {
            toast.error('Please select at least one DGS tag or independent tag.');
            return;
        }
        const tagName = combinedTagAliases;
        const business_rules = businessRules.map(rule => ({
            ALIAS: rule.alias?.value || rule.alias,
            OPERATOR: rule.operator?.value || rule.operator,
            VALUE: rule.value
        }));
        setLoading(true);
        try {
            const response = await startForecast({
                frequency,
                tagName,
                tagAlias: combinedTagAliases,
                datasource_id: datasource,
                startDate: trainingStartDateState,
                endDate: trainingEndDateState,
                forecastStartDate: futureStartDateState,
                forecastEndDate: futureEndDateState,
                name: nameDesc.name,
                desc: nameDesc.desc,
                businessRules: business_rules ?? []
            });
            if (response && response.forecast_id) {
                toast.success('Forecast started successfully!');
            }
            clearFormData();
            await reloadForecastModels();
        } catch (error) {
            console.error('Error while handling startForecast:', error);
            toast.error('Failed to start forecast');
        } finally {
            setLoading(false);
        }
    };
    const handleViewForecastFromTable = async row => {
        setLoading(true);
        // Set flag to skip the useEffect fetch since we're fetching here
        skipFetchRef.current = true;
        try {
            // Update future start and end dates from the clicked model row
            if (row.FUT_ST_DATE) {
                setFutureStartDate(row.FUT_ST_DATE);
            }
            if (row.FUT_END_DATE) {
                setFutureEndDate(row.FUT_END_DATE);
            }
            const data = await getForecastData(
                row.FUT_ST_DATE || futureStartDateState,
                row.FUT_END_DATE || futureEndDateState,
                row.FORECAST_ID
            );
            // Check if data is empty or has no tag_aliases array
            if (!data || !data.tag_aliases || !Array.isArray(data.tag_aliases) || data.tag_aliases.length === 0) {
                setIsNoForecastDataPopup(true);
                setShowForecastChart(false);
                setForecastChartData(null);
                return;
            }
            // Set the forecast chart data for ForecastChart component
            setForecastChartData(data);
            // Store the selected model ID to track which model is currently being viewed
            setSelectedModelName(row.FORECAST_ID);
            // Show the forecast chart
            setShowForecastChart(true);
        } catch (error) {
            console.error('Error fetching forecast data:', error);
            toast.error('Failed to fetch forecast data');
        } finally {
            setLoading(false);
        }
    };
    const reloadForecastModels = async () => {
        if (selectedValues.selectedAssembly) {
            const combinedTagAliases = getCombinedTagAliases(
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            const datasource = getDatasource(
                selectedValues.selectedAssembly,
                selectedValues.selectedDryGasSealTags,
                selectedValues.selectedIndependentTags
            );
            if (!datasource || combinedTagAliases.length === 0) {
                setLiveForecastsData([]);
                return;
            }
            setLoading(true);
            try {
                const forecastsData = await getForecastModelList(
                    datasource,
                    combinedTagAliases
                );
                // Check if forecast list data is empty
                if (!forecastsData || !Array.isArray(forecastsData) || forecastsData.length === 0) {
                    setIsNoExistingModelsPopup(true);
                }
                setLiveForecastsData(forecastsData);
            } catch (error) {
                console.error('Error reloading forecast models:', error);
                toast.error('Failed to reload forecast models');
            } finally {
                setLoading(false);
            }
        }
    };
    const combinedTagAliasesForView = getCombinedTagAliases(
        selectedValues.selectedDryGasSealTags,
        selectedValues.selectedIndependentTags
    );
    let addBusinessRuleButtonClassName = 'button-primary';
    if (!isBusinessRuleAddEnabled) {
        addBusinessRuleButtonClassName = 'button-primary opacity-50 cursor-not-allowed';
    }
    const canStartForecast =
        selectedValues.selectedAssembly &&
        combinedTagAliasesForView.length > 0;
    let forecastButtonClassName = 'button-primary mr-5 mt-8';
    if (!canStartForecast) {
        forecastButtonClassName = 'button-primary mr-5 mt-8 opacity-50 cursor-not-allowed';
    }
    const hasLiveForecastTable =
        liveForecastsData.length !== 0 &&
        selectedValues.selectedAssembly &&
        combinedTagAliasesForView.length > 0;
    const canRenderTrainingChart =
        selectedValues.selectedAssembly &&
        combinedTagAliasesForView.length > 0;
    let trainingChartContent = null;
    if (chartLoading) {
        trainingChartContent = <p className='text-gray-600'>Loading chart. Please wait...</p>;
    } else if (forecastTimeseriesData && forecastTimeseriesData.keys && forecastTimeseriesData.values) {
        trainingChartContent = (
            <TimeSeriesChart
                chartData={forecastTimeseriesData}
                colorTagOrder={selectedTagOrder}
            />
        );
    } else {
        trainingChartContent = null;
    }
    const hasForecastTagAliases =
        forecastChartData &&
        forecastChartData.tag_aliases &&
        forecastChartData.tag_aliases.length !== 0;
    const hasForecastChartData =
        showForecastChart &&
        !forecastChartLoading &&
        hasForecastTagAliases;
    const getBusinessRuleAliasOptions = () => {
        const allAliases = [];
        if (selectedValues.selectedDryGasSealTags?.tagAliasList) {
            const dgsAliases = JSON.parse(
                selectedValues.selectedDryGasSealTags.tagAliasList.replace(/'/g, '"')
            ).map(tag => ({
                label: tag,
                value: tag
            }));
            allAliases.push(...dgsAliases);
        }
        if (selectedValues.selectedIndependentTags && Array.isArray(selectedValues.selectedIndependentTags)) {
            const independentAliases = selectedValues.selectedIndependentTags.map(option => ({
                label: option.tagAlias,
                value: option.tagAlias
            }));
            allAliases.push(...independentAliases);
        }
        return allAliases;
    };
    const renderBusinessRuleRow = (rule, idx) => {
        if (editIndex === idx) {
            const canSaveEditedRule =
                editAlias &&
                editOperator &&
                String(editValue).trim() !== '' &&
                editName.trim() !== '';
            let editSaveButtonClassName = 'button-primary px-2 py-1 text-base';
            if (!canSaveEditedRule) {
                editSaveButtonClassName = 'button-primary px-2 py-1 text-base opacity-50 cursor-not-allowed';
            }
            return (
                <tr key={idx} className='border-b'>
                    <td className='px-3 py-2 w-[210px]'>
                        <Select
                            id={`edit-alias-${idx}`}
                            maxMenuHeight={80}
                            placeholder='Select Alias'
                            options={getBusinessRuleAliasOptions()}
                            value={editAlias}
                            isClearable
                            isSearchable
                            styles={customDropdownStyles}
                            onChange={setEditAlias}
                        />
                    </td>
                    <td className='px-3 py-2 w-[210px]'>
                        <Select
                            id={`edit-operator-${idx}`}
                            maxMenuHeight={80}
                            placeholder='Select Operator'
                            options={[
                                {
                                    label: 'greater',
                                    value: 'greater'
                                },
                                { label: 'lower', value: 'lower' },
                                { label: 'equal', value: 'equal' },
                                {
                                    label: 'not equal',
                                    value: 'not equal'
                                }
                            ]}
                            isClearable
                            styles={customDropdownStyles}
                            value={editOperator}
                            onChange={setEditOperator}
                        />
                    </td>
                    <td className='w-[210px]'>
                        <input
                            type='number'
                            min={0}
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            className='custom-input'
                        />
                    </td>
                    <td className='w-[210px]'>
                        <input
                            type='text'
                            placeholder='CUST_BR_NAME'
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            className='custom-input'
                        />
                    </td>
                    <td className='px-3 py-2 flex gap-2'>
                        <button
                            type='button'
                            className={editSaveButtonClassName}
                            disabled={!canSaveEditedRule}
                            onClick={handleSaveEditBusinessRule}
                        >
                            Save
                        </button>
                        <button
                            type='button'
                            className='button-red px-2 py-1 text-base'
                            onClick={() => handleDeleteBusinessRule(null)}
                        >
                            Delete
                        </button>
                    </td>
                </tr>
            );
        }
        return (
            <tr key={idx} className='border-b'>
                <td className='px-3 py-2'>
                    {rule.alias?.label || rule.alias?.value}
                </td>
                <td className='px-3 py-2'>
                    {rule.operator?.label || rule.operator?.value}
                </td>
                <td className='px-3 py-2'>
                    {rule.value}
                </td>
                <td className='px-3 py-2'>{rule.name}</td>
                <td className='px-3 py-2 flex gap-2'>
                    <button
                        type='button'
                        className='button-primary px-2 py-1 text-base'
                        onClick={() => handleEditBusinessRule(idx)}
                    >
                        Edit
                    </button>
                    <button
                        type='button'
                        className='button-red px-2 py-1 text-base'
                        onClick={() => handleDeleteBusinessRule(idx)}
                    >
                        Delete
                    </button>
                </td>
            </tr>
        );
    };
    return (
        <ForecastFormView
            loading={loading}
            dropdownOptions={dropdownOptions}
            selectedValues={selectedValues}
            errors={errors}
            handleSelectChange={handleSelectChange}
            trainingStartDateState={trainingStartDateState}
            setTrainingStartDate={setTrainingStartDate}
            trainingEndDateState={trainingEndDateState}
            setTrainingEndDate={setTrainingEndDate}
            toggleBusinessRule={toggleBusinessRule}
            isBusinessRules={isBusinessRules}
            getBusinessRuleAliasOptions={getBusinessRuleAliasOptions}
            selectedAlias={selectedAlias}
            setSelectedAlias={setSelectedAlias}
            selectedOperator={selectedOperator}
            setSelectedOperator={setSelectedOperator}
            businessRuleValue={businessRuleValue}
            setBusinessRuleValue={setBusinessRuleValue}
            businessRuleName={businessRuleName}
            setBusinessRuleName={setBusinessRuleName}
            addBusinessRuleButtonClassName={addBusinessRuleButtonClassName}
            isBusinessRuleAddEnabled={isBusinessRuleAddEnabled}
            handleAddBusinessRule={handleAddBusinessRule}
            handleClearAllBusinessRules={handleClearAllBusinessRules}
            businessRules={businessRules}
            renderBusinessRuleRow={renderBusinessRuleRow}
            handleApplyBusinessRules={handleApplyBusinessRules}
            toggleForecastSelection={toggleForecastSelection}
            isForecastSelection={isForecastSelection}
            getCombinedTagAliases={getCombinedTagAliases}
            forecastTagAlias={forecastTagAlias}
            setForecastTagAlias={setForecastTagAlias}
            futureStartDateState={futureStartDateState}
            setFutureStartDate={setFutureStartDate}
            futureEndDateState={futureEndDateState}
            setFutureEndDate={setFutureEndDate}
            frequency={frequency}
            setFrequency={setFrequency}
            nameDesc={nameDesc}
            setNameDesc={setNameDesc}
            handleForecast={handleForecast}
            canStartForecast={canStartForecast}
            forecastButtonClassName={forecastButtonClassName}
            hasLiveForecastTable={hasLiveForecastTable}
            handleViewForecastFromTable={handleViewForecastFromTable}
            liveForecastsData={liveForecastsData}
            updateForecastModelStatus={updateForecastModelStatus}
            setLoading={setLoading}
            deleteForecastModel={deleteForecastModel}
            reloadForecastModels={reloadForecastModels}
            canRenderTrainingChart={canRenderTrainingChart}
            trainingChartContent={trainingChartContent}
            showForecastChart={showForecastChart}
            forecastChartLoading={forecastChartLoading}
            hasForecastChartData={hasForecastChartData}
            forecastChartData={forecastChartData}
            selectedAnomaly={selectedAnomaly}
            forecastTagOrder={forecastTagOrder}
            setSelectedAnomaly={setSelectedAnomaly}
            isNoForecastDataPopup={isNoForecastDataPopup}
            setIsNoForecastDataPopup={setIsNoForecastDataPopup}
            isNoExistingModelsPopup={isNoExistingModelsPopup}
            setIsNoExistingModelsPopup={setIsNoExistingModelsPopup}
            isNoModelsFoundPopup={isNoModelsFoundPopup}
            setIsNoModelsFoundPopup={setIsNoModelsFoundPopup}
        />
    );
};
export default ForecastForm;
